/**
 * Token Storage Utilities
 *
 * Handles encrypted token storage for authentication
 */

// SECURITY: Encryption key for token storage (should be set via env var)
// In production, use a strong key from environment variables
const ENCRYPTION_KEY =
  process.env.NEXT_PUBLIC_ENC_KEY || "kontnt-dev-key-change-in-production";

/**
 * Simple token encryption/decryption (AES-like)
 * SECURITY NOTE: For production, use httpOnly cookies instead of localStorage
 *
 * Refresh tokens MUST be stored in httpOnly cookies (set by backend)
 * Access tokens can remain in encrypted localStorage for MVP
 */
export class TokenStorage {
  private static encrypt(text: string, key: string): string {
    // Simple XOR encryption (for MVP)
    // TODO: Replace with proper AES encryption or use httpOnly cookies
    if (
      process.env.NODE_ENV === "production" &&
      !process.env.NEXT_PUBLIC_ENC_KEY
    ) {
    }
    return btoa(
      text
        .split("")
        .map((char, i) =>
          String.fromCharCode(
            char.charCodeAt(0) ^ key.charCodeAt(i % key.length),
          ),
        )
        .join(""),
    );
  }

  private static decrypt(encrypted: string, key: string): string {
    try {
      const decoded = atob(encrypted);
      return decoded
        .split("")
        .map((char, i) =>
          String.fromCharCode(
            char.charCodeAt(0) ^ key.charCodeAt(i % key.length),
          ),
        )
        .join("");
    } catch {
      return "";
    }
  }

  /**
   * Store access token (encrypted localStorage) with TTL
   * SECURITY: Refresh token should be in httpOnly cookie (set by backend)
   *
   * @param key - Storage key
   * @param value - Value to store
   * @param ttlSeconds - Time to live in seconds (optional, defaults to 15 minutes for access tokens)
   */
  static set(key: string, value: string | null, ttlSeconds?: number) {
    if (typeof window === "undefined") return;
    if (!value) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_expires`);
      return;
    }
    // Encrypt token before storing
    const encrypted = this.encrypt(value, ENCRYPTION_KEY);
    localStorage.setItem(key, encrypted);

    // Store expiration time if TTL is provided
    if (ttlSeconds !== undefined && ttlSeconds > 0) {
      const expiresAt = Date.now() + ttlSeconds * 1000;
      localStorage.setItem(`${key}_expires`, expiresAt.toString());
    }
  }

  static get(key: string): string | null {
    if (typeof window === "undefined") return null;

    // Check if token has expired
    const expiresAt = localStorage.getItem(`${key}_expires`);
    if (expiresAt) {
      const expirationTime = parseInt(expiresAt, 10);
      if (Date.now() >= expirationTime) {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}_expires`);
        return null;
      }
    }
    const encrypted = localStorage.getItem(key);
    if (!encrypted) {
      return null;
    }
    return this.decrypt(encrypted, ENCRYPTION_KEY);
  }

  static remove(key: string) {
    if (typeof window === "undefined") return;
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_expires`);
  }

  /**
   * Check if a stored value has expired
   * @returns true if expired or doesn't exist, false if still valid
   */
  static isExpired(key: string): boolean {
    if (typeof window === "undefined") return true;
    const expiresAt = localStorage.getItem(`${key}_expires`);
    if (!expiresAt) {
      return false; // No TTL set, consider it not expired
    }
    return Date.now() >= parseInt(expiresAt, 10);
  }

  /**
   * Get remaining TTL in seconds for a stored value
   * @returns seconds until expiration, or null if no TTL set
   */
  static getRemainingTTL(key: string): number | null {
    if (typeof window === "undefined") return null;
    const expiresAt = localStorage.getItem(`${key}_expires`);
    if (!expiresAt) {
      return null;
    }
    const expirationTime = parseInt(expiresAt, 10);
    return Math.max(0, Math.floor((expirationTime - Date.now()) / 1000));
  }
}
